// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + glass-card chrome; data layer (queries, mutations)
// untouched.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { createAccount, listAccounts } from '@/lib/grpcweb/console-api'
import { useAuth } from '@/lib/auth/auth-context'
import { DARK, GREY_400, GREY_500, WHITE, errorBlockStyle, primaryButtonCompactStyle } from '@/lib/genesis'

export function AccountsPage() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccounts({}),
  })

  const [name, setName] = useState('')
  const [partnerId, setPartnerId] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () =>
      createAccount({
        name,
        partnerId: partnerId || undefined,
        // Idempotency key generated client-side per task brief step 4 —
        // one fresh key per submit attempt, so a retried submit (e.g. a
        // double-click) is safe, but each distinct user-initiated create
        // is a genuinely new key rather than reusing one across attempts.
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      setName('')
      setPartnerId('')
      setFormError(null)
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onError: () => setFormError('Failed to create account.'),
  })

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Accounts</h1>

      {isAdmin && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            createMutation.mutate()
          }}
          className="glass-card mb-6 flex flex-wrap items-end gap-3 p-4"
        >
          <div>
            <label htmlFor="account-name" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
              Name
            </label>
            <input
              id="account-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="glass-input px-2 py-1.5 text-sm"
              style={{ color: WHITE }}
            />
          </div>
          <div>
            <label htmlFor="account-partner-id" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
              Partner ID (optional)
            </label>
            <input
              id="account-partner-id"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className="glass-input px-2 py-1.5 text-sm"
              style={{ color: WHITE }}
            />
          </div>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            style={primaryButtonCompactStyle}
          >
            {createMutation.isPending ? 'Creating…' : 'Create account'}
          </button>
          {formError && (
            <p className="text-xs" style={{ color: WHITE }}>{formError}</p>
          )}
        </form>
      )}

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading accounts…</p>}
      {isError && (
        <div className="px-3 py-2" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load accounts.</p>
        </div>
      )}
      {!isLoading && !isError && data && data.getAccountsList().length === 0 && (
        <p className="text-sm" style={{ color: GREY_400 }}>No accounts yet.</p>
      )}
      {!isLoading && !isError && data && data.getAccountsList().length > 0 && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left" style={{ borderBottom: `1px solid ${GREY_500}`, color: GREY_400 }}>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Partner</th>
                <th className="px-3 py-2 font-medium">Deal registration</th>
              </tr>
            </thead>
            <tbody>
              {data.getAccountsList().map((acct) => (
                <tr key={acct.getId()} className="transition-colors hover:bg-white/5" style={{ borderBottom: `1px solid ${DARK}` }}>
                  <td className="px-3 py-2">
                    <Link
                      to="/accounts/$accountId"
                      params={{ accountId: acct.getId() }}
                      className="hover:underline"
                      style={{ color: WHITE }}
                    >
                      {acct.getName()}
                    </Link>
                  </td>
                  <td className="px-3 py-2" style={{ color: GREY_400 }}>
                    {acct.getPartnerId() || '—'}
                  </td>
                  <td className="px-3 py-2" style={{ color: GREY_400 }}>
                    {acct.getDealRegistrationId() || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
